import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store } from '@df/catalog/store/store';
import { StoreResource } from '@df/catalog/store/store.resource';
import { StoreService } from '@df/catalog/store/store.service';
import { CheckoutStorePickupComponentDef } from '@df/checkout/checkout-store-pickup.component.def';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { GtmService } from '@df/module-gtm/gtm.service';
import { TimeoutService } from '@df/ng/timeout.service';
import { RadioGroupDirective } from '@df/ui/form/radio/radio.component.def';
import { TranslationModule } from '@df/ui/translation/translation.module';
import compact from 'lodash-es/compact';
import { GTM_FORM_FIELD_ERROR_MESSAGE, LocalGtmService } from '../../module/gtm/gtm.service.decorator';
import { MuiInputDirective } from '../../module/legacy/forms/mui-input.directive';
import { NewsletterCheckboxesComponent } from '../../newsletter/newsletter-checkboxes.component';
import { ActionComponent } from '../../ui/api/action.component';
import { ResultComponent } from '../../ui/api/result.component';
import { InputWrapBodyDirective } from '../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../ui/form/input-wrap.component';
import { Radio3Component } from '../../ui/form/radio3/radio3.component';
import { CheckoutFormComponent } from './../form/checkout-form.component';
import { CheckoutStepComponent } from './../step/checkout-step.component';

const STORES_STEP = 3;
@Component({
    selector: 'checkout-store-pickup',
    templateUrl: './checkout-store-pickup.html',
    styleUrls: ['./checkout-store-pickup.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        TranslationModule,
        CommonModule,
        FormsModule,
        NewsletterCheckboxesComponent,
        ResultComponent,
        ActionComponent,
        MuiInputDirective,
        InputWrapComponent,
        Radio3Component,
        InputWrapBodyDirective,
        RadioGroupDirective,
        NewsletterCheckboxesComponent
    ]
})
export class CheckoutStorePickupComponent extends CheckoutStorePickupComponentDef {
    limit = STORES_STEP;
    resultsView = false;
    allowedStores: Store[] = [];
    protected clicked = false;

    private formErrorSend = false;

    protected timeoutService = inject(TimeoutService);

    constructor(
        public override checkoutFormComponent: CheckoutFormComponent,
        checkoutStepComponent: CheckoutStepComponent,
        changeDetectorRef: ChangeDetectorRef,
        storeService: StoreService,
        app: App,
        storeResource: StoreResource,
        private gtmService: GtmService
    ) {
        super(checkoutFormComponent, checkoutStepComponent, changeDetectorRef, storeService, app, storeResource);
        this.validateCurrentStore();
    }
    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.client.obs.breakpoint.subscribe(() => this.detectChanges()));
        // Update visible stores on basket change. Needed to show store type colection ids
        // in case product that does not allow to store collect is removed from basket on checkout.
        this.subscriptions.add(this.app.customer.basket.subscribe(() => this.updateVisibleStores()));
    }
    get client(): Client {
        return this.app.client;
    }
    getStoreAddress(store: Store): string {
        if (store.data) {
            const addressParts = store.data.address?.split(',').map(i => i.trim());
            return compact([...(addressParts || []), store.data.city, store.data.postcode]).join(', ');
        }
        return '';
    }
    get saving(): boolean {
        return this.clicked && this.saveStatus.busy;
    }
    onClick(): void {
        this.clicked = true;
        this.timeoutService.setTimeout(() => {
            this.detectChanges();
        }, 50);
    }
    get resultsAddress(): string | undefined {
        if (this.geolocationResult) {
            if (this.stores?.[0]) {
                return this.stores[0].data?.postcode;
            }
            return undefined;
        }
        return this.location;
    }
    showMore(): void {
        this.limit = this.limit + STORES_STEP;
        this.detectChanges();
    }
    get displayShowMore(): boolean {
        return this.allowedStores?.length > this.limit;
    }
    reset(): void {
        this.location = '';
        this.resultsView = false;
        this.stores = [];
        this.currentStoreId = undefined;
        this.checkoutStepComponent.markUnsubmitted();
        this.detectChanges();
    }
    continue(): void {
        this.checkoutStepComponent.markSubmitted(true);
        if (this.currentStoreId) {
            return this.checkoutFormComponent.goToNextStep('delivery');
        }
    }
    protected override onInitLoad(): void {
        if (this.currentStoreId) {
            this.app.resources.store.load(this.currentStoreId, 1).then(() => {
                const currentStorePostcode = this.currentStore?.data?.postcode;
                if (currentStorePostcode) {
                    this.location = currentStorePostcode;
                    this.searchStores();
                }
            });
        } else {
            super.onInitLoad();
        }
    }
    protected override onStoresLoad(stores: Store[]): void {
        this.limit = STORES_STEP;
        this.resultsView = true;
        super.onStoresLoad(stores);
        this.updateVisibleStores();
    }
    private validateCurrentStore(): void {
        if (this.currentStoreId && !this.isStoreCollectionAllowed) {
            this.storeResource.load(this.currentStoreId, 3).then(result => {
                // if currently selected id is a store collect and not warehouse collect, and basket content does not allow for store collect,
                // then reset selection and redirect customer to collection step, so he can find a warehouse.
                if (result.data?.allow_for_store_collect) {
                    this.reset();
                }
            });
        }
    }
    private updateVisibleStores(): void {
        this.allowedStores = this.isStoreCollectionAllowed
            ? this.stores
            : this.stores.filter(store => !store.data?.allow_for_store_collect);
        this.detectChanges();
    }
    /**
     * if true, both stores and warehouses are allowed
     * if false, only warehouses are allowed
     */
    private get isStoreCollectionAllowed(): boolean {
        return !this.app.customer.basket.items.find(item => !item.product?.data?.store_collect);
    }

    override submit(): void {
        this.app.request.submitForm(
            this._stepForm,
            undefined,
            () => {
                const promise = this.searchStores();
                if (promise instanceof Promise) {
                    promise.catch(() => {
                        this.sendGA4Error({ error: this.loadStatus.error || '' });
                    });
                }
                return promise;
            },
            this.sendGA4Error
        );
        this.sendGA4Error();
    }

    /**
     * Returns geolocation from browser
     * @returns {IPromise<Coordinates>}
     */
    protected override getGeolocation(): Promise<GeolocationCoordinates> {
        return new Promise((resolve, reject) => {
            window.navigator.geolocation.getCurrentPosition(
                position => {
                    resolve(position.coords);
                },
                () => {
                    // if not enabled we throw error
                    const e = { error: 'Please enable geolocation in your browser' };
                    this.sendGA4Error(e);
                    reject(e);
                },
                {
                    timeout: 10000
                }
            );
        });
    }

    private sendGA4Error = (e?: { error: string }): void => {
        if (e || (!this.formErrorSend && !this._stepForm?.valid)) {
            !e && (this.formErrorSend = true);
            const step = this.checkoutStepComponent;
            (this.gtmService as LocalGtmService).sendFormError(e?.error || GTM_FORM_FIELD_ERROR_MESSAGE, 'checkout collection', 2);
        }
    };
}
